export interface PageResult<TPage, TCursor> {
  page: TPage;
  nextCursor: TCursor | null | undefined;
}

export interface PageIterator<TPage> extends AsyncIterableIterator<TPage, undefined, undefined> {
  return(): Promise<IteratorReturnResult<undefined>>;
}

/**
 * Yields pages one at a time. The next page is only requested after the consumer has asked for it,
 * and it is addressed by the cursor of the previous page, so pages are never fetched concurrently.
 * Stops after the first page whose `nextCursor` is null or undefined. A rejected fetch rejects the iteration
 * and ends it.
 * Each `next()` call fetches one page from the cursor kept in this closure, so there is no await inside a loop
 * and stack depth stays constant however many pages there are. Overlapping `next()` calls are queued in order.
 */
export function paginate<TPage, TCursor>(
  fetchPage: (cursor: TCursor | undefined) => Promise<PageResult<TPage, TCursor>>,
  initialCursor?: TCursor
): PageIterator<TPage> {
  let cursor = initialCursor;
  let finished = false;
  let queue: Promise<unknown> = Promise.resolve();

  const finish = (): IteratorReturnResult<undefined> => {
    finished = true;
    return { done: true, value: undefined };
  };

  const fetchNext = async (): Promise<IteratorResult<TPage, undefined>> => {
    if (finished) {
      return finish();
    }
    try {
      const { page, nextCursor } = await fetchPage(cursor);
      if (nextCursor === null || nextCursor === undefined) {
        finished = true;
      } else {
        cursor = nextCursor;
      }
      return { done: false, value: page };
    } catch (error) {
      finished = true;
      throw error;
    }
  };

  const enqueue = <T>(step: () => Promise<T> | T): Promise<T> => {
    const result = queue.then(step);
    queue = result.catch(() => undefined);
    return result;
  };

  const iterator: PageIterator<TPage> = {
    next: () => enqueue(fetchNext),
    return: () => enqueue(finish),
    [Symbol.asyncIterator]: () => iterator,
  };
  return iterator;
}
